<template>
  <div class="members-page">
    <!-- Page Header -->
    <div class="page-header mb-6">
      <div class="d-flex justify-space-between align-center">
        <div>
          <h1 class="text-h4 font-weight-bold text-primary mb-2">Board Members</h1>
          <p class="text-body-1 text-medium-emphasis">
            Manage board members, roles, and committee assignments
          </p>
        </div>
        
        <div class="d-flex align-center gap-3">
          <v-btn
            v-if="isAdmin"
            color="primary"
            prepend-icon="mdi-account-plus"
            @click="addMember"
          >
            Add Member
          </v-btn>
          
          <v-btn
            color="secondary"
            variant="outlined"
            prepend-icon="mdi-export"
            @click="exportMembers"
          >
            Export
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Filters and Search -->
    <v-card class="mb-6">
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="4">
            <v-text-field
              v-model="searchQuery"
              placeholder="Search members..."
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            />
          </v-col>
          
          <v-col cols="12" md="2">
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              label="Status"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            />
          </v-col>
          
          <v-col cols="12" md="2">
            <v-select
              v-model="roleFilter"
              :items="roleOptions"
              label="Role"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            />
          </v-col>
          
          <v-col cols="12" md="2">
            <v-select
              v-model="committeeFilter"
              :items="committeeOptions"
              label="Committee"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            />
          </v-col>
          
          <v-col cols="12" md="2">
            <div class="d-flex align-center gap-2">
              <v-btn-toggle
                v-model="viewMode"
                mandatory
                density="compact"
                variant="outlined"
              >
                <v-btn value="grid" icon>
                  <v-icon>mdi-view-grid</v-icon>
                </v-btn>
                <v-btn value="list" icon>
                  <v-icon>mdi-view-list</v-icon>
                </v-btn>
              </v-btn-toggle>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" size="64" />
      <p class="text-h6 mt-4">Loading members...</p>
    </div>

    <!-- Members Grid View -->
    <div v-else-if="viewMode === 'grid'" class="members-grid">
      <v-row>
        <v-col
          v-for="member in filteredMembers"
          :key="member.id"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <v-card
            class="member-card"
            @click="viewMember(member)"
            hover
          >
            <div class="d-flex flex-column align-center pa-6">
              <v-avatar
                :color="member.avatar ? undefined : 'primary'"
                size="80"
                class="mb-4"
              >
                <v-img
                  v-if="member.avatar"
                  :src="getMemberPhotoUrl(member)"
                  :alt="member.full_name"
                />
                <span v-else class="text-h4 font-weight-bold">
                  {{ getInitials(member.full_name) }}
                </span>
              </v-avatar>
              
              <h3 class="text-h6 font-weight-bold text-center mb-2">
                {{ member.full_name }}
              </h3>
              
              <v-chip
                :color="getRoleColor(member.role)"
                size="small"
                variant="flat"
                class="mb-2"
              >
                {{ member.role }}
              </v-chip>
              
              <v-chip
                :color="getStatusColor(member.status)"
                size="small"
                variant="outlined"
                class="mb-3"
              >
                {{ member.status }}
              </v-chip>
              
              <div class="member-stats d-flex justify-space-around w-100 mb-3">
                <div class="text-center">
                  <div class="text-h6 font-weight-bold">{{ member.committees_count || 0 }}</div>
                  <div class="text-caption text-medium-emphasis">Committees</div>
                </div>
                <div class="text-center">
                  <div class="text-h6 font-weight-bold">{{ member.attendance_rate || 0 }}%</div>
                  <div class="text-caption text-medium-emphasis">Attendance</div>
                </div>
              </div>
              
              <div class="text-center">
                <div class="text-body-2 text-medium-emphasis">{{ member.email }}</div>
                <div v-if="member.phone" class="text-body-2 text-medium-emphasis">{{ member.phone }}</div>
              </div>
            </div>
            
            <!-- Action Menu -->
            <v-card-actions class="justify-center">
              <v-btn
                size="small"
                variant="text"
                @click.stop="viewMember(member)"
              >
                View Profile
              </v-btn>
              
              <v-menu v-if="isAdmin" offset-y>
                <template #activator="{ props }">
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    v-bind="props"
                    @click.stop
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="editMember(member)">
                    <v-list-item-title>Edit</v-list-item-title>
                    <template #prepend>
                      <v-icon>mdi-pencil</v-icon>
                    </template>
                  </v-list-item>
                  <v-list-item @click="manageMemberCommittees(member)">
                    <v-list-item-title>Committees</v-list-item-title>
                    <template #prepend>
                      <v-icon>mdi-account-group</v-icon>
                    </template>
                  </v-list-item>
                  <v-list-item @click="resetMemberPassword(member)">
                    <v-list-item-title>Reset Password</v-list-item-title>
                    <template #prepend>
                      <v-icon>mdi-lock-reset</v-icon>
                    </template>
                  </v-list-item>
                  <v-divider />
                  <v-list-item 
                    @click="deactivateMember(member)"
                    :class="{ 'text-error': member.status === 'Active' }"
                  >
                    <v-list-item-title>
                      {{ member.status === 'Active' ? 'Deactivate' : 'Activate' }}
                    </v-list-item-title>
                    <template #prepend>
                      <v-icon>
                        {{ member.status === 'Active' ? 'mdi-account-off' : 'mdi-account-check' }}
                      </v-icon>
                    </template>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Members List View -->
    <div v-else class="members-list">
      <v-card>
        <v-data-table
          :headers="tableHeaders"
          :items="filteredMembers"
          :loading="loading"
          :search="searchQuery"
          item-value="id"
          class="elevation-0"
          @click:row="viewMember"
        >
          <template #[`item.full_name`]="{ item }">
            <div class="d-flex align-center">
              <v-avatar
                :color="item.avatar ? undefined : 'primary'"
                size="40"
                class="mr-3"
              >
                <v-img
                  v-if="item.avatar"
                  :src="getMemberPhotoUrl(item)"
                  :alt="item.full_name"
                />
                <span v-else class="text-subtitle-2 font-weight-bold">
                  {{ getInitials(item.full_name) }}
                </span>
              </v-avatar>
              <div>
                <div class="font-weight-bold">{{ item.full_name }}</div>
                <div class="text-body-2 text-medium-emphasis">{{ item.email }}</div>
              </div>
            </div>
          </template>
          
          <template #[`item.role`]="{ item }">
            <v-chip
              :color="getRoleColor(item.role)"
              size="small"
              variant="flat"
            >
              {{ item.role }}
            </v-chip>
          </template>
          
          <template #[`item.status`]="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              size="small"
              variant="outlined"
            >
              {{ item.status }}
            </v-chip>
          </template>
          
          <template #[`item.committees`]="{ item }">
            <div class="d-flex flex-wrap gap-1">
              <v-chip
                v-for="committee in (item.committees || []).slice(0, 2)"
                :key="committee.id"
                size="x-small"
                variant="outlined"
              >
                {{ committee.name }}
              </v-chip>
              <v-chip
                v-if="(item.committees || []).length > 2"
                size="x-small"
                variant="text"
              >
                +{{ (item.committees || []).length - 2 }} more
              </v-chip>
            </div>
          </template>
          
          <template #[`item.attendance_rate`]="{ item }">
            <div class="d-flex align-center">
              <v-progress-circular
                :model-value="item.attendance_rate || 0"
                :color="getAttendanceColor(item.attendance_rate)"
                size="24"
                width="3"
                class="mr-2"
              />
              <span>{{ item.attendance_rate || 0 }}%</span>
            </div>
          </template>
          
          <template #[`item.term_start`]="{ item }">
            {{ formatDate(item.term_start) }}
          </template>
          
          <template #[`item.term_end`]="{ item }">
            {{ formatDate(item.term_end) }}
          </template>
          
          <template #[`item.actions`]="{ item }">
            <div class="d-flex align-center gap-1">
              <v-btn
                icon
                size="small"
                variant="text"
                @click.stop="viewMember(item)"
              >
                <v-icon>mdi-eye</v-icon>
              </v-btn>
              
              <v-btn
                v-if="isAdmin"
                icon
                size="small"
                variant="text"
                @click.stop="editMember(item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              
              <v-menu v-if="isAdmin" offset-y>
                <template #activator="{ props }">
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    v-bind="props"
                    @click.stop
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list density="compact">
                  <v-list-item @click="manageMemberCommittees(item)">
                    <v-list-item-title>Manage Committees</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="resetMemberPassword(item)">
                    <v-list-item-title>Reset Password</v-list-item-title>
                  </v-list-item>
                  <v-divider />
                  <v-list-item @click="deactivateMember(item)">
                    <v-list-item-title>
                      {{ item.status === 'Active' ? 'Deactivate' : 'Activate' }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && filteredMembers.length === 0" class="text-center py-12">
      <v-icon size="120" color="grey-lighten-2">mdi-account-group-outline</v-icon>
      <h3 class="text-h5 font-weight-medium mt-4 mb-2">No Members Found</h3>
      <p class="text-body-1 text-medium-emphasis mb-4">
        {{ searchQuery ? 'Try adjusting your search or filters' : 'No board members have been added yet' }}
      </p>
      <v-btn
        v-if="isAdmin && !searchQuery"
        color="primary"
        @click="addMember"
      >
        Add First Member
      </v-btn>
    </div>

    <!-- Add/Edit Member Dialog -->
    <v-dialog v-model="showMemberDialog" max-width="800" persistent>
      <v-card>
        <v-card-title class="text-h6">
          {{ editingMember ? 'Edit Member' : 'Add New Member' }}
        </v-card-title>
        
        <v-card-text>
          <v-form ref="memberForm" v-model="memberFormValid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="memberData.first_name"
                  label="First Name"
                  variant="outlined"
                  :rules="[rules.required]"
                  required
                />
              </v-col>
              
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="memberData.last_name"
                  label="Last Name"
                  variant="outlined"
                  :rules="[rules.required]"
                  required
                />
              </v-col>
              
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="memberData.email"
                  label="Email"
                  type="email"
                  variant="outlined"
                  :rules="[rules.required, rules.email]"
                  required
                />
              </v-col>
              
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="memberData.phone"
                  label="Phone"
                  variant="outlined"
                  :rules="[rules.phone]"
                />
              </v-col>
              
              <v-col cols="12" md="6">
                <v-select
                  v-model="memberData.role"
                  :items="roleOptions"
                  label="Role"
                  variant="outlined"
                  :rules="[rules.required]"
                  required
                />
              </v-col>
              
              <v-col cols="12" md="6">
                <v-select
                  v-model="memberData.status"
                  :items="statusOptions"
                  label="Status"
                  variant="outlined"
                  :rules="[rules.required]"
                  required
                />
              </v-col>
              
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="memberData.department"
                  label="Department"
                  variant="outlined"
                />
              </v-col>
              
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="memberData.term_start"
                  label="Term Start Date"
                  type="date"
                  variant="outlined"
                />
              </v-col>
              
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="memberData.term_end"
                  label="Term End Date"
                  type="date"
                  variant="outlined"
                />
              </v-col>
              
              <v-col cols="12">
                <v-select
                  v-model="memberData.committees"
                  :items="allCommittees"
                  item-title="name"
                  item-value="id"
                  label="Committee Assignments"
                  variant="outlined"
                  multiple
                  chips
                />
              </v-col>
              
              <v-col cols="12">
                <v-textarea
                  v-model="memberData.bio"
                  label="Biography"
                  variant="outlined"
                  rows="3"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer />
          <v-btn @click="cancelMemberDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!memberFormValid"
            :loading="savingMember"
            @click="saveMember"
          >
            {{ editingMember ? 'Update' : 'Add' }} Member
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Committee Management Dialog -->
    <v-dialog v-model="showCommitteeDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h6">
          Manage Committee Assignments - {{ selectedMember?.full_name }}
        </v-card-title>
        
        <v-card-text>
          <v-select
            v-model="selectedMemberCommittees"
            :items="allCommittees"
            item-title="name"
            item-value="id"
            label="Committee Assignments"
            variant="outlined"
            multiple
            chips
            closable-chips
          />
        </v-card-text>
        
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showCommitteeDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="updatingCommittees"
            @click="updateMemberCommittees"
          >
            Update Assignments
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/store/auth'
import api, { API_BASE_URL } from '@/services/api'

export default {
  name: 'Members',
  setup() {
    const router = useRouter()
    const toast = useToast()
    const authStore = useAuthStore()

    // API Base URL
    const apiBaseUrl = API_BASE_URL

    // State
    const loading = ref(true)
    const members = ref([])
    const allCommittees = ref([])
    const searchQuery = ref('')
    const statusFilter = ref(null)
    const roleFilter = ref(null)
    const committeeFilter = ref(null)
    const viewMode = ref('grid')
    const isAdmin = computed(() => authStore.user?.is_admin || false)

    // Dialog states
    const showMemberDialog = ref(false)
    const showCommitteeDialog = ref(false)
    const editingMember = ref(null)
    const selectedMember = ref(null)
    const memberFormValid = ref(false)
    const savingMember = ref(false)
    const updatingCommittees = ref(false)

    // Form data
    const memberData = ref({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      role: '',
      status: 'Active',
      department: '',
      term_start: '',
      term_end: '',
      committees: [],
      bio: ''
    })

    const selectedMemberCommittees = ref([])

    // Options
    const statusOptions = ['Active', 'Inactive', 'Emeritus', 'Honorary']
    const roleOptions = ['Chairman', 'Vice Chairman', 'Secretary', 'Treasurer', 'Member', 'Observer']

    const committeeOptions = computed(() => {
      return allCommittees.value.map(committee => ({
        title: committee.name,
        value: committee.id
      }))
    })

    // Table headers
    const tableHeaders = [
      { title: 'Name', key: 'full_name', sortable: true },
      { title: 'Role', key: 'role', sortable: true },
      { title: 'Status', key: 'status', sortable: true },
      { title: 'Committees', key: 'committees', sortable: false },
      { title: 'Attendance', key: 'attendance_rate', sortable: true },
      { title: 'Term Start', key: 'term_start', sortable: true },
      { title: 'Term End', key: 'term_end', sortable: true },
      { title: 'Actions', key: 'actions', sortable: false, align: 'center' }
    ]

    // Validation rules
    const rules = {
      required: value => !!value || 'Field is required',
      email: value => {
        const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return pattern.test(value) || 'Invalid email address'
      },
      phone: value => {
        if (!value) return true
        const pattern = /^[+]?[1-9][\d]{0,15}$/
        return pattern.test(value) || 'Invalid phone number'
      }
    }

    // Computed
    const filteredMembers = computed(() => {
      let filtered = members.value

      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        filtered = filtered.filter(member =>
          member.full_name.toLowerCase().includes(query) ||
          member.email.toLowerCase().includes(query) ||
          member.role.toLowerCase().includes(query)
        )
      }

      if (statusFilter.value) {
        filtered = filtered.filter(member => member.status === statusFilter.value)
      }

      if (roleFilter.value) {
        filtered = filtered.filter(member => member.role === roleFilter.value)
      }

      if (committeeFilter.value) {
        filtered = filtered.filter(member =>
          member.committees?.some(committee => committee.id === committeeFilter.value)
        )
      }

      return filtered
    })

    // Methods
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString()
    }

    const getInitials = (name) => {
      if (!name || typeof name !== 'string') {
        return 'NA'
      }
      return name.split(' ').map(part => part[0]).join('').toUpperCase()
    }

    const getRoleColor = (role) => {
      const colors = {
        'Chairman': 'purple',
        'Vice Chairman': 'indigo',
        'Secretary': 'blue',
        'Treasurer': 'green',
        'Member': 'grey',
        'Observer': 'orange'
      }
      return colors[role] || 'grey'
    }

    const getStatusColor = (status) => {
      const colors = {
        'Active': 'success',
        'Inactive': 'error',
        'Emeritus': 'warning',
        'Honorary': 'info'
      }
      return colors[status] || 'grey'
    }

    const getAttendanceColor = (rate) => {
      if (rate >= 80) return 'success'
      if (rate >= 60) return 'warning'
      return 'error'
    }

    const getMemberPhotoUrl = (member) => {
      if (!member.avatar) return null
      return `${apiBaseUrl}/members/${member.id}/photo?t=${Date.now()}`
    }

    const viewMember = (member) => {
      router.push(`/members/${member.id}`)
    }

    const addMember = () => {
      editingMember.value = null
      memberData.value = {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: '',
        status: 'Active',
        department: '',
        term_start: '',
        term_end: '',
        committees: [],
        bio: ''
      }
      showMemberDialog.value = true
    }

    const editMember = (member) => {
      editingMember.value = member
      memberData.value = {
        first_name: member.first_name,
        last_name: member.last_name,
        email: member.email,
        phone: member.phone || '',
        role: member.role,
        status: member.status,
        department: member.department || '',
        term_start: member.term_start || '',
        term_end: member.term_end || '',
        committees: member.committees?.map(c => c.id) || [],
        bio: member.bio || ''
      }
      showMemberDialog.value = true
    }

    const cancelMemberDialog = () => {
      showMemberDialog.value = false
      editingMember.value = null
      memberData.value = {}
    }

    const saveMember = async () => {
      savingMember.value = true
      try {
        console.log('Auth status:', authStore.isAuthenticated)
        console.log('User:', authStore.user)
        console.log('Token:', authStore.token)
        
        const data = {
          first_name: memberData.value.first_name,
          last_name: memberData.value.last_name,
          email: memberData.value.email,
          phone: memberData.value.phone,
          role: memberData.value.role, // This will be mapped to 'position' by the backend
          department: memberData.value.department || '',
          bio: memberData.value.bio || '',
          term_start: memberData.value.term_start,
          term_end: memberData.value.term_end,
          committees: memberData.value.committees || []
        }

        console.log('Saving member data:', data)

        if (editingMember.value) {
          const response = await api.put(`/members/${editingMember.value.id}`, data)
          console.log('Update response:', response.data)
          toast.success('Member updated successfully')
        } else {
          const response = await api.post('/members', data)
          console.log('Create response:', response.data)
          toast.success('Member added successfully')
        }

        showMemberDialog.value = false
        loadMembers()
      } catch (error) {
        console.error('Error saving member:', error)
        console.error('Error response:', error.response?.data)
        console.error('Error status:', error.response?.status)
        
        const errorMessage = error.response?.data?.message || 'Failed to save member'
        toast.error(errorMessage)
      } finally {
        savingMember.value = false
      }
    }

    const manageMemberCommittees = (member) => {
      selectedMember.value = member
      selectedMemberCommittees.value = member.committees?.map(c => c.id) || []
      showCommitteeDialog.value = true
    }

    const updateMemberCommittees = async () => {
      updatingCommittees.value = true
      try {
        console.log('Updating committees for member:', selectedMember.value.id)
        console.log('Committee IDs to assign:', selectedMemberCommittees.value)
        console.log('Available committees:', allCommittees.value)
        
        const response = await api.put(`/members/${selectedMember.value.id}/committees`, {
          committee_ids: selectedMemberCommittees.value
        })
        
        console.log('Committee update response:', response.data)
        toast.success('Committee assignments updated')
        showCommitteeDialog.value = false
        loadMembers()
      } catch (error) {
        console.error('Error updating committees:', error)
        console.error('Error response:', error.response?.data)
        console.error('Error status:', error.response?.status)
        
        const errorMessage = error.response?.data?.message || 'Failed to update committee assignments'
        toast.error(errorMessage)
      } finally {
        updatingCommittees.value = false
      }
    }

    const resetMemberPassword = async (member) => {
      try {
        await api.post(`/members/${member.id}/reset-password`)
        toast.success('Password reset email sent')
      } catch (error) {
        console.error('Error resetting password:', error)
        toast.error('Failed to reset password')
      }
    }

    const deactivateMember = async (member) => {
      try {
        if (member.status === 'Active') {
          await api.put(`/members/${member.id}/deactivate`)
          toast.success('Member deactivated')
        } else {
          await api.put(`/members/${member.id}/activate`)
          toast.success('Member activated')
        }
        loadMembers()
      } catch (error) {
        console.error('Error updating member status:', error)
        toast.error('Failed to update member status')
      }
    }

    const exportMembers = () => {
      // TODO: Implement export functionality
      toast.info('Export functionality coming soon')
    }

    // Data loading
    const loadMembers = async () => {
      loading.value = true
      try {
        const [membersResponse, committeesResponse] = await Promise.all([
          api.get('/members'),
          api.get('/committees')
        ])
        

        
        // Ensure we have valid data structures
        const membersData = membersResponse.data.members || []
        const committeesData = committeesResponse.data.committees || []
        
        // Validate member data and add default values for missing fields
        members.value = membersData.map(member => ({
          id: member.id || 0,
          full_name: member.full_name || member.name || 'Unknown Name',
          first_name: member.user?.first_name || member.first_name || '',
          last_name: member.user?.last_name || member.last_name || '',
          email: member.email || member.user?.email || '',
          phone: member.phone || member.user?.phone || '',
          role: member.position || 'Member',  // Backend uses 'position' field
          status: member.is_current !== false ? 'Active' : 'Inactive',  // Backend uses 'is_current' boolean
          department: member.department || '',
          term_start: member.start_date || '',
          term_end: member.end_date || '',
          attendance_rate: member.attendance_rate || 0,
          committees_count: member.committees_count || (member.committees ? member.committees.length : 0),
          avatar: member.avatar || member.profile_image || null,
          bio: member.bio || '',
          committees: member.committees || []
        }))
        
        allCommittees.value = committeesData
        
        console.log('Loaded members:', members.value)
        console.log('Loaded committees:', allCommittees.value)
      } catch (error) {
        console.error('Error loading data:', error)
        toast.error('Failed to load members')
        // Set empty arrays as fallback
        members.value = []
        allCommittees.value = []
      } finally {
        loading.value = false
      }
    }

    // Watch for debugging
    watch(memberFormValid, (newVal) => {
      console.log('Member form valid:', newVal)
    })

    watch(memberData, (newVal) => {
      console.log('Member data:', newVal)
    }, { deep: true })

    // Lifecycle
    onMounted(() => {
      loadMembers()
    })

    return {
      // State
      loading,
      members,
      allCommittees,
      searchQuery,
      statusFilter,
      roleFilter,
      committeeFilter,
      viewMode,
      isAdmin,

      // Dialog states
      showMemberDialog,
      showCommitteeDialog,
      editingMember,
      selectedMember,
      memberFormValid,
      savingMember,
      updatingCommittees,

      // Form data
      memberData,
      selectedMemberCommittees,

      // Options
      statusOptions,
      roleOptions,
      committeeOptions,
      tableHeaders,
      rules,

      // Computed
      filteredMembers,

      // Methods
      formatDate,
      getInitials,
      getRoleColor,
      getStatusColor,
      getAttendanceColor,
      getMemberPhotoUrl,
      viewMember,
      addMember,
      editMember,
      cancelMemberDialog,
      saveMember,
      manageMemberCommittees,
      updateMemberCommittees,
      resetMemberPassword,
      deactivateMember,
      exportMembers
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables.scss";

.members-page {
  .text-primary {
    color: rgb(var(--v-theme-primary)) !important;
  }

  .text-medium-emphasis {
    opacity: 0.7;
  }

  .member-card {
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    }

    .member-stats {
      border-top: 1px solid rgba(0, 0, 0, 0.12);
      padding-top: 16px;
    }
  }

  .members-grid {
    .v-col {
      transition: all 0.3s ease;
    }
  }

  .members-list {
    .v-data-table {
      ::v-deep(.v-data-table__tr) {
        cursor: pointer;
        
        &:hover {
          background-color: rgba(var(--v-theme-primary), 0.04);
        }
      }
    }
  }

  .page-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    padding-bottom: 24px;
  }
}
</style>